import React, { useState } from 'react';
import { Form, Input, Button, Typography, Space, Divider, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { logger } from '../../utils/logger';
import {
  LoginContainer,
  StyledCard,
  LogoContainer,
  Logo
} from './styles/Login.styles';

const { Title, Text } = Typography;

interface LoginForm {
    email: string;
    password: string;
}

const Login: React.FC = () => {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // Get the redirect path from location state or default to home page
    const from = (location.state as any)?.from?.pathname || '/';

    const onFinish = async (values: LoginForm) => {
        setLoading(true);
        try {
            await login(values.email, values.password);
            logger.info('Successful login:', { email: values.email });
            // Redirect to the page the user was trying to access
            navigate(from);
        } catch (error: any) {
            logger.error('Login form error:', error);

            // Проверяем, является ли ошибка связанной с неподтвержденным email
            if (error?.response?.data?.code === 'EMAIL_NOT_VERIFIED' || 
                (error?.response?.status === 403 && (
                    error?.response?.data?.message?.includes('Email not verified') ||
                    error?.response?.data?.message?.includes('Email не подтвержден')
                ))) {
                message.warning({
                    content: error?.response?.data?.message || 'Email не подтвержден. Пожалуйста, проверьте почту и подтвердите регистрацию.',
                    duration: 5
                });
            } else {
                message.error('Неверный email или пароль');
                form.setFields([
                    {
                        name: 'password',
                        errors: ['Неверный email или пароль']
                    }
                ]);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <LoginContainer>
            <StyledCard>
                <LogoContainer>
                    <Logo>KARATE-ONLINE</Logo>
                    <Text type="secondary">Система управления турнирами</Text>
                </LogoContainer>

                <Title level={3} style={{ textAlign: 'center', marginBottom: 32 }}>
                    Вход в систему
                </Title>

                <Form
                    form={form}
                    name="login"
                    onFinish={onFinish}
                    layout="vertical"
                    size="large"
                >
                    <Form.Item
                        name="email"
                        rules={[
                            { required: true, message: 'Пожалуйста, введите email' },
                            { type: 'email', message: 'Пожалуйста, введите корректный email' }
                        ]}
                    >
                        <Input
                            prefix={<UserOutlined />}
                            placeholder="Email"
                            disabled={loading}
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        rules={[{ required: true, message: 'Пожалуйста, введите пароль' }]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Пароль"
                            disabled={loading}
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            block
                            size="large"
                        >
                            Войти
                        </Button>
                    </Form.Item>

                    <Divider plain>или</Divider>

                    <Space direction="vertical" size="small" style={{ width: '100%', textAlign: 'center' }}>
                        <Link to="/register">
                            <Button type="link" block>
                                Зарегистрироваться
                            </Button>
                        </Link>
                        <Link to="/forgot-password">
                            <Button type="link" block>
                                Забыли пароль?
                            </Button>
                        </Link>
                    </Space>
                </Form>
            </StyledCard>
        </LoginContainer>
    );
};

export default Login;